import { LocalStoreService } from "../LocalStoreService";

const LOG_PREFIX = "[AppSyncEvent]";

export function operationStatusAppSyncTraceEnabled() {
    const o = publicSettings?.aws?.operationStatusAppSync;
    if (o?.trace === false) return false;
    if (o?.trace === true) return true;
    return publicSettings?.environment === "development";
}

function logTrace(...args) {
    if (operationStatusAppSyncTraceEnabled()) {
        console.info(LOG_PREFIX, ...args);
    }
}

function describeAppSyncWsErrors(errors) {
    if (!Array.isArray(errors) || errors.length === 0) return "";
    return errors
        .map((e) => {
            if (e && typeof e === "object") {
                const parts = [e.errorType, e.errorCode, e.message].filter(
                    (x) => typeof x === "string" && x.length > 0
                );
                return parts.length ? parts.join(" · ") : JSON.stringify(e);
            }
            return String(e);
        })
        .join("; ");
}

function summarizeWsMessage(msg) {
    if (!msg || typeof msg !== "object") return msg;
    const { type, id, errors } = msg;
    const out = { type, id, errors };
    if (msg.payload !== undefined) {
        const raw =
            typeof msg.payload === "string"
                ? msg.payload
                : JSON.stringify(msg.payload);
        out.payloadPreview = raw.length > 500 ? `${raw.slice(0, 500)}…` : raw;
    }
    if (msg.event !== undefined) {
        const raw =
            typeof msg.event === "string"
                ? msg.event
                : JSON.stringify(msg.event);
        out.eventPreview = raw.length > 500 ? `${raw.slice(0, 500)}…` : raw;
    }
    return out;
}

function base64UrlFromAuthObject(obj) {
    const json = JSON.stringify(obj);
    return btoa(json)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}

function normalizeAppsyncEventHttpHost(raw) {
    const s = (raw || "").trim().replace(/^https?:\/\//i, "");
    if (!s) return "";
    const slash = s.indexOf("/");
    return slash === -1 ? s : s.slice(0, slash);
}

function normalizeAppsyncRealtimeHost(raw) {
    const s = (raw || "").trim().replace(/^wss?:\/\//i, "");
    if (!s) return "";
    const lower = s.toLowerCase();
    const suffix = "/event/realtime";
    if (lower.endsWith(suffix)) {
        return s.slice(0, -suffix.length).replace(/\/+$/, "");
    }
    const slash = s.indexOf("/");
    return slash === -1 ? s : s.slice(0, slash);
}

function getJwtToken() {
    try {
        const token = new LocalStoreService().getItem("nucont:jwt_token");
        if (typeof token !== "string" || !token.length) return "";
        return token.replace(/^Bearer\s+/i, "").trim();
    } catch {
        return "";
    }
}

export class AppSyncEventsSubscription {
    constructor({ httpHost, realtimeHost }) {
        this.httpHost = normalizeAppsyncEventHttpHost(httpHost);
        this.realtimeHost = normalizeAppsyncRealtimeHost(realtimeHost);
        this.ws = null;
        this.connected = false;
        this.subscriptionHandlers = {};
        this._connectPromise = null;
    }

    _authProtocol() {
        const jwt = getJwtToken();
        const header = base64UrlFromAuthObject({
            Authorization: jwt,
            host: this.httpHost
        });
        return `header-${header}`;
    }

    connect() {
        if (this._connectPromise != null) return this._connectPromise;
        if (!this.httpHost || !this.realtimeHost) {
            return Promise.reject(
                new Error(
                    "AppSyncEventsSubscription: defina httpHost (hostname do endpoint HTTP da Event API, sem path /event) e realtimeHost"
                )
            );
        }
        if (!getJwtToken()) {
            return Promise.reject(
                new Error(
                    "AppSyncEventsSubscription: JWT ausente (nucont:jwt_token); AppSync pode negar a conexão sem invocar a Lambda"
                )
            );
        }
        this._connectPromise = new Promise((resolve, reject) => {
            const url = `wss://${this.realtimeHost}/event/realtime`;
            logTrace("connect iniciando", {
                realtimeHost: this.realtimeHost,
                httpHost: this.httpHost,
                hasToken: Boolean(getJwtToken())
            });
            const protocols = [this._authProtocol(), "aws-appsync-event-ws"];
            try {
                this.ws = new WebSocket(url, protocols);
            } catch (e) {
                console.error(LOG_PREFIX, "connect WebSocket throw", e);
                reject(e);
                return;
            }
            let acked = false;
            this.ws.onerror = (err) => {
                console.error(LOG_PREFIX, "WebSocket onerror", err);
                if (!acked) {
                    let reason;
                    if (err instanceof Error) {
                        reason = err;
                    } else if (typeof err === "string") {
                        reason = new Error(err);
                    } else {
                        reason = new Error("WebSocket error");
                    }
                    reject(reason);
                }
            };
            this.ws.onopen = () => {
                logTrace("WebSocket open, enviando connection_init");
                this.ws.send(JSON.stringify({ type: "connection_init" }));
            };
            this.ws.onmessage = (evt) => {
                let msg;
                try {
                    msg = JSON.parse(evt.data);
                } catch (parseErr) {
                    console.warn(LOG_PREFIX, "mensagem não-JSON", {
                        preview: String(evt.data).slice(0, 200),
                        parseMessage:
                            parseErr instanceof Error
                                ? parseErr.message
                                : String(parseErr)
                    });
                    return;
                }
                if (msg.type === "connection_ack") {
                    logTrace("connection_ack", summarizeWsMessage(msg));
                    if (!acked) {
                        acked = true;
                        this.connected = true;
                        resolve();
                    }
                    return;
                }
                if (msg.type === "connection_error") {
                    const errDetail = describeAppSyncWsErrors(msg.errors);
                    console.error(
                        LOG_PREFIX,
                        "connection_error: AppSync recusou a conexão (handshake/auth).",
                        errDetail || "(sem detalhe extra)",
                        summarizeWsMessage(msg)
                    );
                    if (!acked) {
                        if (this.ws) {
                            this.ws.close();
                            this.ws = null;
                        }
                        this._connectPromise = null;
                        const hint =
                            "Causas comuns: Lambda retornou isAuthorized false; token inválido/expirado; host do subprotocolo diferente do endpoint HTTP da API; regex do authorizer no console; modo de auth da operação Connect não é AWS_LAMBDA.";
                        reject(
                            new Error(
                                `AppSync WebSocket 401 — ${
                                    errDetail ||
                                    JSON.stringify(msg.errors || msg)
                                }. ${hint}`
                            )
                        );
                    }
                    return;
                }
                if (msg.type === "ka") {
                    logTrace("keep-alive (ka)");
                    return;
                }
                if (msg.type === "subscribe_success") {
                    logTrace("subscribe_success", summarizeWsMessage(msg));
                    const h = this.subscriptionHandlers[msg.id];
                    if (h?.onSuccess) h.onSuccess();
                    return;
                }
                if (msg.type === "subscribe_error") {
                    console.warn(
                        LOG_PREFIX,
                        "subscribe_error (resposta AppSync)",
                        summarizeWsMessage(msg)
                    );
                    const h = this.subscriptionHandlers[msg.id];
                    if (h?.onError) h.onError(msg);
                    return;
                }
                if (msg.type === "data") {
                    logTrace("data (evento recebido)", summarizeWsMessage(msg));
                    const h = this.subscriptionHandlers[msg.id];
                    if (h?.onData) h.onData(msg);
                    return;
                }
                logTrace("mensagem ws não tratada", summarizeWsMessage(msg));
            };
        });
        return this._connectPromise;
    }

    subscribe(subscriptionId, channel, { onData, onError, onSuccess } = {}) {
        if (!this.ws || !this.connected) {
            throw new Error(
                "AppSyncEventsSubscription: connect antes de subscribe"
            );
        }
        logTrace("subscribe enviado", { subscriptionId, channel });
        this.subscriptionHandlers[subscriptionId] = {
            onData,
            onError,
            onSuccess
        };
        this.ws.send(
            JSON.stringify({
                type: "subscribe",
                id: subscriptionId,
                channel,
                authorization: {
                    Authorization: getJwtToken(),
                    host: this.httpHost
                }
            })
        );
    }

    unsubscribe(subscriptionId) {
        logTrace("unsubscribe", { subscriptionId });
        if (this.ws && this.connected) {
            this.ws.send(
                JSON.stringify({
                    type: "unsubscribe",
                    id: subscriptionId
                })
            );
        }
        delete this.subscriptionHandlers[subscriptionId];
    }

    close() {
        logTrace("close");
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.connected = false;
        this._connectPromise = null;
        this.subscriptionHandlers = {};
    }
}
